const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const ACCESS = "nexora_admin_access";

export const loggedIn = () => Boolean(sessionStorage.getItem(ACCESS));
export const clear = () => sessionStorage.removeItem(ACCESS);
export const save = (access: string) => sessionStorage.setItem(ACCESS, access);

let rotatePromise: Promise<boolean> | null = null;

async function performRotate(): Promise<boolean> {
  const response = await safeFetch(`${BASE}/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) return false;
  const data = await response.json() as { accessToken: string };
  save(data.accessToken);
  return true;
}

function rotate(): Promise<boolean> {
  rotatePromise ??= performRotate().finally(() => { rotatePromise = null; });
  return rotatePromise;
}

// Restores a protected 30-day server session when the browser opens a new tab.
// The refresh credential never leaves the HttpOnly cookie.
export const resume = rotate;

export async function call<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const token = sessionStorage.getItem(ACCESS);
  const hasBody = init.body !== undefined && init.body !== null;
  const response = await safeFetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (response.status === 401 && retry && await rotate()) return call<T>(path, init, false);
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string; error?: string; fields?: Array<{ path: string; message: string }> };
    const fieldMessage = body.fields?.map((field) => `${adminFieldName(field.path)}: ${field.message}`).join("; ");
    throw new Error(body.message ?? fieldMessage ?? friendlyError(body.error, response.status));
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const adminFieldName = (path: string) => ({ slug: "Код", name: "Название", description: "Описание", priceRub: "Цена", durationDays: "Срок", maxDevices: "Устройства", trafficLimitBytes: "Лимит трафика" } as Record<string, string>)[path] ?? path;
const friendlyError = (code: string | undefined, status: number) => ({ VALIDATION_ERROR: "Проверьте заполненные поля", INTERNAL_ERROR: "Внутренняя ошибка. Нажмите «Обновить»; если повторится — откройте журнал API." } as Record<string, string>)[code ?? ""] ?? code ?? `Ошибка HTTP ${status}`;

async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(15_000) });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    if (cause instanceof DOMException && cause.name === "TimeoutError") {
      throw new Error("Сервер не ответил вовремя. Нажмите «Обновить» через несколько секунд.");
    }
    throw new Error("Нет связи с Nexora Core. Проверьте интернет или VPN и нажмите «Обновить».");
  }
}

export async function logout(): Promise<void> {
  try {
    await call<void>("/v1/auth/logout", { method: "POST" });
  } catch {
    // Always remove the local access token, even when the API is unavailable.
  } finally {
    clear();
  }
}
